import { useRef, useState } from "react";
import { useSelector } from "react-redux";
import { IconSize } from "~/common/components/icon/IconSize";
import { IconType } from "~/common/components/icon/IconType";
import { LinkButton } from "~/common/components/Link";
import { MenuBottomCornersRounded, useSimpleMenuState } from "~/common/components/Menu/Menu";
import { MENU_POS_RIGHT } from "~/common/components/Menu/MenuPositions";
import { useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import type { RootState } from "~/app/store";
import { isProductPage } from "~/utils/url/urlUtils";
import classes from "~/features/shareButton/ShareButton.module.less";
import { Icon } from "~/common/components/icon/Icon";
import { MenuItem } from "~/common/components/Menu/MenuItem";
import { MICROSOFT_TEAMS } from "~/utils/url/staticResources";
import { useTranslation } from "react-i18next";
import { C_PRIMARY } from "~/common/components/icon/Colors";
import { resolveTranslationSync } from "~/app/i18n/Translation";

const COPY_TO_CLIPBOARD = {
  icon: IconType.Link,
  text: "share.copy",
};
const COPIED_TO_CLIPBOARD = {
  icon: IconType.Check,
  text: "share.copied",
};

const extractCodeFromUrl = () => {
  const pathName = window.location.pathname;
  const parts = pathName.split("/");
  return parts[parts.length - 1];
};

const ShareButton = () => {
  const [isOpen, handleClick, handleClose, anchorEl, setAnchorEl] = useSimpleMenuState();
  const [clipboardMessage, setClipboardMessage] = useState(COPY_TO_CLIPBOARD);
  const isPdp = isProductPage();
  const isTablet = useIsMinTabletSize();
  const { t } = useTranslation();

  const getProductBasicInfo = (state: RootState) => state?.products.basic[extractCodeFromUrl()];
  const basicProductData = useSelector(getProductBasicInfo);

  const subject = t("share.subject", { product: basicProductData?.designation });

  const message = `${t("share.intro", {})}
${basicProductData?.designation}
${basicProductData && resolveTranslationSync(basicProductData?.shortDescription)}
${window.location.href}`;

  const messageWithoutUrl = `${t("share.intro", {})}
${basicProductData?.designation}
${basicProductData && resolveTranslationSync(basicProductData?.shortDescription)}`;

  const shareToMail = () => {
    const URI = `mailto:?subject=${encodeURI(subject)}&body=${encodeURI(message)}`;
    window.location.href = URI;
  };

  const shareToMSTeams = () => {
    const URI = `MSTeams:/l/chat/0/0?users=&message=${encodeURI(message)}`;
    window.location.href = URI;
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    setClipboardMessage(COPIED_TO_CLIPBOARD);
  };

  const divRef = useRef(null);

  const onClick = () => setAnchorEl(divRef.current);

  if (!isPdp) {
    return;
  }

  const mobileShare = async () => {
    try {
      await navigator.share({
        title: subject,
        text: messageWithoutUrl,
        url: window.location.href,
      });
    } catch (e) {
      console.error(e);
    }
  };

  if (isTablet) {
    return (
      <div ref={divRef}>
        <LinkButton className={classes.share} onClick={onClick}>
          <Icon
            className={"icon-margin-l scha__header__language--icon"}
            type={IconType.Share}
            size={IconSize.FONT_20}
          />
          <span>{t("page.wishlist.action.share")}</span>
        </LinkButton>
        <MenuBottomCornersRounded
          anchorEl={anchorEl}
          open={isOpen}
          {...MENU_POS_RIGHT}
          onClose={() => {
            handleClose();
            setClipboardMessage(COPY_TO_CLIPBOARD);
          }}
          MenuListProps={{ disablePadding: true }}
        >
          <MenuItem className={classes.menuItem} onClick={shareToMail}>
            <Icon color={C_PRIMARY} type={IconType.Envelope} />
            {t("share.email")}
          </MenuItem>
          <MenuItem className={classes.menuItem} onClick={shareToMSTeams}>
            <img src={MICROSOFT_TEAMS} />
            {t("share.msteams")}
          </MenuItem>
          <MenuItem className={classes.menuItem} onClick={copyToClipboard}>
            <Icon size={IconSize.FONT_20} type={clipboardMessage.icon} />
            {t(clipboardMessage.text)}
          </MenuItem>
        </MenuBottomCornersRounded>
      </div>
    );
  }

  return (
    <LinkButton className={classes.share} onClick={mobileShare}>
      <Icon className={"icon-margin-l scha__header__language--icon"} type={IconType.Share} size={IconSize.FONT_20} />
    </LinkButton>
  );
};

export default ShareButton;
